/**
 * Tipos internos da tela de compras.
 *
 * Papel: aqui ficam os formatos que o react-hook-form guarda para o pedido e
 * para o item, e os dois valores em branco de onde a criação parte.
 *
 * Os dois são derivados dos schemas do contrato com `z.input`, e não escritos à
 * mão, porque é a entrada daqueles schemas que o formulário precisa produzir.
 */
import { type novoItemPedidoSchema, type novoPedidoCompraSchema } from '@concierge/shared';
import { type z } from 'zod';

/** O pedido como o formulário o guarda, antes de o schema normalizar. */
export type PedidoNoFormulario = z.input<typeof novoPedidoCompraSchema>;

/** O item como o formulário o guarda. */
export type ItemNoFormulario = z.input<typeof novoItemPedidoSchema>;

/**
 * Um item em branco, como uma linha recém-acrescentada começa.
 *
 * `quantidade` começa em 1 porque é a quantidade da maioria esmagadora dos
 * itens, e `statusFinanceiro` em `provisionado` porque o item nasce num pedido
 * que ainda nem foi aprovado — marcá-lo `pago` de saída afirmaria uma saída de
 * caixa que não houve.
 *
 * `foto` fica **ausente**, e não vazia: ausente significa "não mexa", e é o que
 * preserva a imagem de um item já gravado quando o formulário o reenvia. Uma
 * linha nova também não tem `id`, e é por isso que ela nasce sem imagem — a API
 * casa a imagem pela identidade do item, nunca pela posição dele na lista.
 */
export const ITEM_EM_BRANCO: ItemNoFormulario = {
  descricao: '',
  quantidade: 1,
  valorUnitario: '',
  desconto: '',
  acrescimo: '',
  fornecedor: '',
  categoriaFinanceira: '',
  subcategoriaFinanceira: '',
  statusFinanceiro: 'provisionado',
  dataCompra: '',
  parcelas: '',
  intervaloParcelamento: undefined,
};

/**
 * Um pedido em branco, como o formulário de criação começa.
 *
 * `status` começa em `em_cotacao`, que é a primeira etapa do funil: é onde o
 * pedido nasce, antes de se saber o preço e às vezes antes de se saber o item.
 *
 * `itens` começa com uma linha, e não vazio: um pedido sem item nenhum é válido,
 * mas quem abre o formulário quase sempre já sabe o que quer pedir.
 */
export const PEDIDO_EM_BRANCO: PedidoNoFormulario = {
  residenciaId: '',
  numero: '',
  data: '',
  status: 'em_cotacao',
  solicitante: '',
  canalOrigem: 'presencial',
  categoria: 'outros',
  observacao: '',
  aprovador: '',
  dataAprovacao: '',
  compradoPor: '',
  motivoReprovacao: '',
  lancadoNoFinanceiro: false,
  itens: [ITEM_EM_BRANCO],
};
